/**
 * Cookie Consent Management Service
 * Integrates vanilla-cookieconsent with Google Consent Mode v2
 * Implements GDPR-compliant cookie consent for Rulevia
 */

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';
import { getConsentModeUpdate } from './cookie-categories';

// Extend window object for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface ConsentPreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

class CookieConsentService {
  private isInitialized = false;
  private currentConsent: ConsentPreferences = {
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  };

  /**
   * Initialize the cookie consent banner
   */
  async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      const config = this.getConsentConfig();

      CookieConsent.run(config);
      this.isInitialized = true;

    } catch (error) {
      console.error('❌ Failed to initialize cookie consent:', error);
    }
  }

  /**
   * Show the cookie preferences modal
   */
  show(): void {
    if (!this.isInitialized) {
      return;
    }
    CookieConsent.showPreferences();
  }
  
  /**
   * Show the main consent modal (for first time visitors)
   */
  showConsentModal(): void {
    if (!this.isInitialized) {
      return;
    }
    CookieConsent.show();
  }

  /**
   * Hide the cookie consent modal
   */
  hide(): void {
    if (!this.isInitialized) return;
    CookieConsent.hide();
  }

  /**
   * Get current consent preferences
   */
  getConsent(): ConsentPreferences {
    if (!this.isInitialized) return this.currentConsent;
    
    return {
      necessary: true, // Always true
      functional: CookieConsent.acceptedCategory('functional'),
      analytics: CookieConsent.acceptedCategory('analytics'),
      marketing: CookieConsent.acceptedCategory('marketing')
    };
  }

  /**
   * Check if a specific category is consented
   */
  hasConsent(category: keyof ConsentPreferences): boolean {
    if (!this.isInitialized) return this.currentConsent[category];
    if (category === 'necessary') return true;
    return CookieConsent.acceptedCategory(category);
  }

  /**
   * Update Google Consent Mode based on user preferences
   */
  private updateConsentMode(categories: Record<string, boolean>): void {
    if (typeof window === 'undefined' || !window.gtag) return;

    const consentUpdate = getConsentModeUpdate(categories);
    
    try {
      window.gtag('consent', 'update', consentUpdate);
    } catch (error) {
      console.error('❌ Failed to update consent mode:', error);
    }
  }

  /**
   * Handle first consent action
   */
  private onFirstConsent = ({ cookie }: any): void => {
    this.handleConsentUpdate(cookie);
  };

  /**
   * Handle any consent action
   */
  private onConsent = ({ cookie }: any): void => {
    this.handleConsentUpdate(cookie);
  };

  /**
   * Handle consent changes
   */
  private onChange = ({ cookie }: any): void => {
    this.handleConsentUpdate(cookie);
  };

  /**
   * Process consent updates
   */
  private handleConsentUpdate(cookie: any): void {
    const acceptedCategories = cookie.categories || [];
    const consent: Record<string, boolean> = {
      necessary: true,
      functional: acceptedCategories.includes('functional'),
      analytics: acceptedCategories.includes('analytics'),
      marketing: acceptedCategories.includes('marketing')
    };

    this.currentConsent = consent as ConsentPreferences;
    this.updateConsentMode(consent);
    
    // Trigger custom event for other services
    window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { 
      detail: { consent, acceptedCategories }
    }));

  }

  /**
   * Generate CookieConsent configuration
   */
  private getConsentConfig(): any {
    return {
      // Cookie settings
      cookie: {
        name: 'cc_cookie',
        expiresAfterDays: 365,
        domain: window.location.hostname,
        path: '/',
        sameSite: 'Lax'
      },

      // Callback functions
      onFirstConsent: this.onFirstConsent,
      onConsent: this.onConsent,
      onChange: this.onChange,

      // Categories configuration
      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        functional: {
          enabled: true,
          readOnly: false
        },
        analytics: {
          enabled: false,
          readOnly: false
        },
        marketing: {
          enabled: false,
          readOnly: false
        }
      },

      // GUI options
      guiOptions: {
        consentModal: {
          layout: 'cloud',
          position: 'bottom center',
          transition: 'slide',
          equalWeightButtons: true,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          transition: 'slide',
          equalWeightButtons: true,
          flipButtons: false
        }
      },

      // Language configuration
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We value your privacy',
              description: 'We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies as described in our Cookie Policy.',
              acceptAllBtn: 'Accept All',
              acceptNecessaryBtn: 'Reject All',
              showPreferencesBtn: 'Manage Preferences',
              footer: `
                <a href="/privacy">Privacy Policy</a>
                <a href="/cookies">Cookie Policy</a>
              `
            },
            preferencesModal: {
              title: 'Cookie Preferences',
              acceptAllBtn: 'Accept All',
              acceptNecessaryBtn: 'Reject All',
              savePreferencesBtn: 'Save Preferences',
              closeIconLabel: 'Close',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Cookie Usage',
                  description: 'We use cookies to ensure basic website functionality, provide personalized features, and analyze how our website is used. You can review your choices and change your preferences at any time by clicking on the cookie icon. <a href="/cookies">Cookie Policy</a> | <a href="/privacy">Privacy Policy</a>'
                },
                {
                  title: 'Essential Cookies',
                  description: 'These cookies are strictly necessary for our website to function properly and cannot be disabled without impacting core functionality.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Functional Cookies',
                  description: 'These cookies enhance your experience by remembering your preferences and settings. They are enabled by default based on legitimate interest but can be disabled.',
                  linkedCategory: 'functional',
                  cookieTable: {
                    headers: {
                      name: 'Service',
                      domain: 'Purpose',
                      expiration: 'Expiry'
                    },
                    body: [
                      {
                        name: 'User Preferences',
                        domain: 'Dashboard settings and UI preferences',
                        expiration: '30 days'
                      },
                      {
                        name: 'AI Model Preferences',
                        domain: 'AI model selection and generation settings',
                        expiration: '30 days'
                      }
                    ]
                  }
                },
                {
                  title: 'Analytics Cookies',
                  description: 'These cookies help us understand how visitors use our website. They allow us to measure performance and improve user experience.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: {
                      name: 'Service',
                      domain: 'Purpose',
                      expiration: 'Expiry'
                    },
                    body: [
                      {
                        name: 'Google Analytics',
                        domain: 'Website performance analysis and user behavior insights',
                        expiration: '2 years'
                      },
                      {
                        name: 'Microsoft Clarity',
                        domain: 'User behavior analytics and session recordings',
                        expiration: '1 year'
                      },
                      {
                        name: 'Sentry Monitoring',
                        domain: 'Error monitoring and performance tracking',
                        expiration: 'Session'
                      }
                    ]
                  }
                },
                {
                  title: 'Marketing Cookies',
                  description: 'These cookies are used for marketing analytics, campaign attribution, and improving our advertising effectiveness.',
                  linkedCategory: 'marketing',
                  cookieTable: {
                    headers: {
                      name: 'Service',
                      domain: 'Purpose',
                      expiration: 'Expiry'
                    },
                    body: [
                      {
                        name: 'Meta (Facebook) Pixel',
                        domain: 'Marketing campaign performance and retargeting',
                        expiration: '3 months'
                      },
                      {
                        name: 'Reddit Pixel',
                        domain: 'Advertising attribution and audience insights',
                        expiration: '3 months'
                      },
                      {
                        name: 'Google Tag Manager',
                        domain: 'Campaign tracking and marketing analytics',
                        expiration: '2 years'
                      }
                    ]
                  }
                }
              ]
            }
          }
        }
      }
    };
  }


  /**
   * Reset all consent (for testing)
   */
  reset(): void {
    if (!this.isInitialized) return;
    CookieConsent.reset(true);
    this.currentConsent = {
      necessary: true,
      functional: true,
      analytics: false,
      marketing: false
    };
  }
  
  /**
   * Debug method to test cookie consent
   */
  debug(): void {
    // Add global debug methods
    if (typeof window !== 'undefined') {
      (window as any).cookieConsentDebug = {
        show: () => this.show(),
        showConsent: () => this.showConsentModal(),
        reset: () => this.reset(),
        debug: () => this.debug(),
        getConsent: () => this.getConsent()
      };
    }
  }
}

// Create singleton instance
export const cookieConsent = new CookieConsentService();

// Export types
export type { ConsentPreferences };
export { CookieConsentService };

// Export default instance
export default cookieConsent;